import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { categories, MAIN_SITE_URL } from '@/mocks/categories';

const navItems = [
  { key: 'nav_home', to: '/', icon: 'ri-home-4-line' },
  { key: 'nav_spreadsheet', to: '/#what', icon: 'ri-grid-line' },
  { key: 'nav_guides', to: '/#guides', icon: 'ri-book-open-line' },
  { key: 'nav_reviews', to: '/#info', icon: 'ri-star-line' },
  { key: 'nav_shipping', to: '/#info', icon: 'ri-truck-line' },
  { key: 'nav_safety', to: '/#safety', icon: 'ri-shield-check-line' },
  { key: 'nav_faq', to: '/#faq', icon: 'ri-question-line' },
];

export default function Header() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
            <i className="ri-grid-fill text-lg"></i>
          </span>
          <span className="text-base font-bold text-foreground-950 whitespace-nowrap">
            {t('brand_name')}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.to}
              className="flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700 cursor-pointer whitespace-nowrap"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={`text-sm ${item.icon}`}></i>
              </span>
              {t(item.key)}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-shopping-bag-3-line text-sm"></i>
            </span>
            {t('nav_shoppingGuide')}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-background-300 text-foreground-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[75vh] overflow-y-auto border-t border-background-200 bg-white px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className={`text-base ${item.icon}`}></i>
                </span>
                {t(item.key)}
              </Link>
            ))}
          </nav>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('nav_browseCategories')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.link}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-lg border border-background-200 bg-white px-3 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary-500">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                  <span className="truncate">{t(cat.nameKey)}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-4 flex items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-shopping-bag-3-line text-sm"></i>
            </span>
            {t('nav_shoppingGuide')}
          </a>
        </div>
      )}
    </header>
  );
}